// Category dropdowns for the Admin / Super Admin toolbars. One trigger per
// category keeps the toolbar to a handful of controls; anything an operator
// must not miss (SMS blocked, unmatched faxes) goes on the trigger as a badge.
//
//   ActionMenu  — a list of actions (Chakra Menu).
//   ActionPanel — a popover for on/off settings, filled with ToggleRow.
import React from "react";
import {
  Badge,
  Box,
  Button,
  Flex,
  IconButton,
  Menu,
  MenuButton,
  MenuDivider,
  MenuGroup,
  MenuItem,
  MenuList,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Portal,
  Spinner,
  Switch,
  Text,
  Tooltip,
  useDisclosure,
} from "@chakra-ui/react";
import { ChevronDownIcon, SettingsIcon } from "@chakra-ui/icons";

const TRIGGER_SIZE = { base: "xs", sm: "sm", md: "md" };
const TRIGGER_FONT = { base: "10px", sm: "xs", md: "sm" };

const TriggerLabel = ({ label, badge }) => (
  <Flex align="center" gap={2} whiteSpace="nowrap">
    <Text as="span" fontSize={TRIGGER_FONT}>
      {label}
    </Text>
    {badge ? (
      <Badge
        colorScheme={badge.colorScheme || "red"}
        variant="solid"
        borderRadius="full"
        fontSize="0.65em"
        px={2}
      >
        {badge.text}
      </Badge>
    ) : null}
  </Flex>
);

/**
 * sections: [{ title?, items: [{ label, onClick, isDisabled?, isLoading?,
 *              hint?, color? }] }]
 * Falsy items/sections are skipped, so callers can inline conditions.
 */
export const ActionMenu = ({
  label,
  colorScheme = "gray",
  variant = "solid",
  badge,
  sections = [],
}) => {
  const visible = sections
    .filter(Boolean)
    .map((s) => ({ ...s, items: (s.items || []).filter(Boolean) }))
    .filter((s) => s.items.length);

  return (
    <Menu isLazy placement="bottom-start">
      <MenuButton
        as={Button}
        colorScheme={colorScheme}
        variant={variant}
        size={TRIGGER_SIZE}
        px={{ base: 2, sm: 4 }}
        rightIcon={<ChevronDownIcon />}
      >
        <TriggerLabel label={label} badge={badge} />
      </MenuButton>
      <Portal>
        <MenuList zIndex="dropdown" maxH="70vh" overflowY="auto" minW="240px">
          {visible.map((section, si) => {
            const items = section.items.map((item) => (
              <MenuItem
                key={item.key || item.label}
                onClick={item.onClick}
                isDisabled={item.isDisabled || item.isLoading}
                color={item.color}
                closeOnSelect
              >
                <Box>
                  <Flex align="center" gap={2}>
                    <Text fontSize="sm">{item.label}</Text>
                    {item.isLoading ? <Spinner size="xs" /> : null}
                  </Flex>
                  {item.hint ? (
                    <Text fontSize="xs" color="gray.500">
                      {item.hint}
                    </Text>
                  ) : null}
                </Box>
              </MenuItem>
            ));
            return (
              <React.Fragment key={section.title || si}>
                {si > 0 ? <MenuDivider /> : null}
                {section.title ? (
                  <MenuGroup title={section.title}>{items}</MenuGroup>
                ) : (
                  items
                )}
              </React.Fragment>
            );
          })}
        </MenuList>
      </Portal>
    </Menu>
  );
};

/**
 * A popover of settings. `children` may be a function receiving `close`, so
 * a row that opens a modal can shut the popover first (the popover's z-index
 * sits above Chakra modals).
 */
export const ActionPanel = ({
  label,
  colorScheme = "gray",
  variant = "outline",
  badge,
  children,
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  return (
    <Popover
      isOpen={isOpen}
      onOpen={onOpen}
      onClose={onClose}
      placement="bottom-start"
      isLazy
    >
      <PopoverTrigger>
        <Button
          colorScheme={colorScheme}
          variant={variant}
          size={TRIGGER_SIZE}
          px={{ base: 2, sm: 4 }}
          rightIcon={<ChevronDownIcon />}
        >
          <TriggerLabel label={label} badge={badge} />
        </Button>
      </PopoverTrigger>
      <Portal>
        <PopoverContent w="320px" maxW="95vw">
          <PopoverArrow />
          <PopoverBody p={2}>
            {typeof children === "function" ? children(onClose) : children}
          </PopoverBody>
        </PopoverContent>
      </Portal>
    </Popover>
  );
};

/**
 * One on/off row in an ActionPanel. `onLabelClick` turns the label into a
 * link (e.g. open the dashboard); `onSettingsClick` adds a gear button.
 */
export const ToggleRow = ({
  label,
  description,
  isChecked,
  onChange,
  isDisabled,
  colorScheme = "blue",
  danger = false,
  onLabelClick,
  onSettingsClick,
  settingsLabel,
}) => {
  const tone = danger && !isChecked ? "red" : isChecked ? colorScheme : "gray";
  return (
    <Flex
      align="center"
      gap={2}
      px={2}
      py={2}
      borderRadius="md"
      bg={danger && !isChecked ? "red.50" : undefined}
      _hover={{ bg: danger && !isChecked ? "red.100" : "gray.50" }}
    >
      <Box flex="1" minW={0}>
        <Text
          as={onLabelClick ? "button" : "span"}
          type={onLabelClick ? "button" : undefined}
          fontSize="sm"
          fontWeight="semibold"
          color={`${tone}.700`}
          textAlign="left"
          _hover={
            onLabelClick ? { textDecoration: "underline", cursor: "pointer" } : undefined
          }
          onClick={onLabelClick}
        >
          {label}
        </Text>
        {description ? (
          <Text fontSize="xs" color="gray.500" noOfLines={2}>
            {description}
          </Text>
        ) : null}
      </Box>
      {onSettingsClick ? (
        <Tooltip label={settingsLabel || `${label} settings`} hasArrow>
          <IconButton
            aria-label={settingsLabel || `${label} settings`}
            icon={<SettingsIcon />}
            size="xs"
            variant="ghost"
            colorScheme={colorScheme}
            onClick={onSettingsClick}
          />
        </Tooltip>
      ) : null}
      <Switch
        size="sm"
        colorScheme={colorScheme}
        isChecked={isChecked}
        onChange={onChange}
        isDisabled={isDisabled}
        aria-label={label}
      />
    </Flex>
  );
};
